import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import apiUtils from "utils/apiutils";
import ActiveBotsTable from "./ActiveBotsTable";
import Icon from "./AppIcon";

function ActiveBotTabs() {
  const [activeTab, setActiveTab] = useState("tp_sl");
  const [bots, setBots] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const strategyName = activeTab.toUpperCase();

  const fetchBots = async () => {
    setIsLoading(true);
    const payloads = {
      page_no: pageNo,
      page_size: pageSize,
      strategy_name: strategyName,
      // trade_type:"LIVE"
    };

    try {
      const resp = await apiUtils.post("/bot/view_bot", payloads, {
        showToast: false,
      });
      if (resp?.Success) {
        setBots(resp.Success);
      } else {
        setBots([]);
      }
    } catch (e) {
      console.error("Error fetching bots:", e);
      setBots([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTotalPages = async () => {
    const payloads = {
      page_no: 1,
      page_size: pageSize,
      strategy_name: strategyName,
      // trade_type:"LIVE"
    };

    try {
      const resp = await apiUtils.post("/bot/total_view_bot", payloads, {
        showToast: false,
      });
      if (resp?.Success != null) {
        setTotalPages(resp.Success);
      } else {
        setTotalPages(0);
      }
    } catch (e) {
      console.error("Error fetching total pages:", e);
      setTotalPages(0);
    }
  };

  useEffect(() => {
    fetchBots();
  }, [activeTab, pageNo, pageSize]);

  useEffect(() => {
    fetchTotalPages();
  }, [activeTab, pageSize]);

  useEffect(() => {
    if (totalPages > 0 && pageNo > totalPages) {
      setPageNo(totalPages);
    } else if (totalPages === 0) {
      setPageNo(1);
    }
  }, [totalPages]);

  const tabs = [
    { id: "tp_sl", label: "Tp/Sl", icon: "Settings" },
    { id: "liquidation", label: "Liquidation", icon: "BarChart3" },
  ];

  const pageSizeOptions = [10, 20, 50, 100];

  return (
    <div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-card border border-border rounded-xl mb-6 overflow-hidden"
      >
          <h2 className="text-xl font-bold m-4">Active Bots</h2>
        <div className="flex border-b border-border-secondary">
          <div className="md:hidden flex-1 relative">
            <select
              value={activeTab}
              onChange={(e) => {
                setActiveTab(e.target.value);
                setPageNo(1);
              }}
              className="w-full px-4 py-3 text-sm font-medium text-left border-b-2 border-transparent text-muted-foreground bg-transparent focus:border-primary focus:text-primary focus:outline-none appearance-none cursor-pointer"
            >
              {tabs.map((tab) => (
                <option
                  key={tab.id}
                  value={tab.id}
                  className="bg-card text-foreground"
                >
                  {tab.label}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
              <Icon name="ChevronDown" size={16} className="text-muted-foreground" />
            </div>
          </div>

          <div className="hidden md:flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setPageNo(1);
                }}
                className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-all ${
                  activeTab === tab.id
                    ? "border-primary text-primary bg-primary/5"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <ActiveBotsTable
          bots={bots}
          isLoading={isLoading}
          pageNo={pageNo}
          pageSize={pageSize}
          strategyName={strategyName}
          fetchBots={fetchBots}
          fetchTotalPages={fetchTotalPages}
        />

        {totalPages > 0 && (
          <div className="px-4 py-3 border-t border-border-secondary">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-muted-foreground">
              <div className="order-2 md:order-1">
                Page {pageNo} of {totalPages} ({bots.length} bots on this page)
              </div>

              <div className="flex items-center gap-3 order-1 md:order-2">
                <button
                  onClick={() => setPageNo(1)}
                  disabled={isLoading || pageNo === 1}
                  className="p-2 rounded-lg bg-card border border-border hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <Icon name="ChevronsLeft" size={14} />
                </button>
                <button
                  onClick={() => setPageNo(pageNo - 1)}
                  disabled={isLoading || pageNo === 1}
                  className="p-2 rounded-lg bg-card border border-border hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <Icon name="ChevronLeft" size={14} />
                </button>

                <div className="flex items-center gap-2">
                  <span>Page</span>
                  <input
                    type="number"
                    value={pageNo}
                    onChange={(e) => {
                      let p = parseInt(e.target.value) || 1;
                      p = Math.max(1, Math.min(p, totalPages));
                      setPageNo(p);
                    }}
                    min={1}
                    max={totalPages}
                    className="w-14 px-2 py-1 text-center bg-card border border-border rounded focus:border-primary focus:outline-none"
                    disabled={isLoading}
                  />
                  <span>of {totalPages}</span>
                </div>

                <button
                  onClick={() => setPageNo(pageNo + 1)}
                  disabled={isLoading || pageNo >= totalPages}
                  className="p-2 rounded-lg bg-card border border-border hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <Icon name="ChevronRight" size={14} />
                </button>
                <button
                  onClick={() => setPageNo(totalPages)}
                  disabled={isLoading || pageNo === totalPages}
                  className="p-2 rounded-lg bg-card border border-border hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <Icon name="ChevronsRight" size={14} />
                </button>
              </div>

              <div className="flex items-center gap-2 order-3">
                <span>Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    const newSize = Number(e.target.value);
                    setPageSize(newSize);
                    setPageNo(1);
                  }}
                  disabled={isLoading}
                  className="bg-card border border-border rounded px-3 py-1.5 focus:border-primary focus:outline-none cursor-pointer"
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default ActiveBotTabs;